import React, { useEffect, useState } from 'react';
import Navbar from '../Navbar/Navbar';
import './Partner.scss';
import api from '../Axios/axios';

// TrackingPage'deki tarih parse ve kontrol fonksiyonları:

// "dd.MM.yyyy" => Date objesi
function parseTrDateString(dateString) {
  if (!dateString) return null;
  const [day, month, year] = dateString.split('.');
  if (!day || !month || !year) return null;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

// Süresi geçmiş mi?
function isOverdue(endDateString) {
  if (!endDateString) return false;
  const endDate = parseTrDateString(endDateString);
  if (!endDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return endDate < today;
}

// 1 ay içinde mi bitiyor?
function isNearEndDate(endDateString) {
  if (!endDateString) return false;
  const endDate = parseTrDateString(endDateString);
  if (!endDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diff = endDate - today;
  const daysDiff = diff / (1000 * 60 * 60 * 24);
  // 0 <= daysDiff <= 30 => 1 ay içinde bitiyor
  return daysDiff >= 0 && daysDiff <= 30;
}

// ISO formatındaki tarihten "dd.MM.yyyy" üret
function formatTrDate(isoDateString) {
  if (!isoDateString) return '-';
  const dateObj = new Date(isoDateString);
  if (isNaN(dateObj.getTime())) return '-';

  const day = dateObj.getDate();
  const month = dateObj.getMonth() + 1;
  const year = dateObj.getFullYear();
  const dayStr = day < 10 ? '0' + day : '' + day;
  const monthStr = month < 10 ? '0' + month : '' + month;
  return `${dayStr}.${monthStr}.${year}`;
}

const Partner = () => {
  const [towerCranes, setTowerCranes] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [brands, setBrands] = useState([]);

  // TrackingPage tarzı filtre nesnesi:
  const [filters, setFilters] = useState({
    brand: '',
    model: '',
    height: '',
    tonnage: '',
    status: 'all', // "all" | "Active" | "Inused"
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1) Kule Vinçler
        const craneResponse = await api.get('/TowerCrane');
        const craneData = craneResponse.data.data || [];

        // 2) Rental
        const rentalResponse = await api.get('/Rental');
        const rentalData = rentalResponse.data.data || [];

        // 3) Brand (Markalar)
        const brandResponse = await api.get('/Brand');
        const brandData = brandResponse.data.data || [];

        setTowerCranes(craneData);
        setRentals(rentalData);
        setBrands(brandData);
      } catch (error) {
        console.error('Veriler yüklenirken hata oluştu:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filtre değişimi
  const handleFilterChange = (e, filterName) => {
    setFilters({
      ...filters,
      [filterName]: e.target.value,
    });
  };

  // Markayı ID'den bul
  const getBrandName = (brandId) => {
    const brand = brands.find((b) => b.id === brandId);
    return brand ? brand.name : '-';
  };

  // TrackingPage'deki getRentalDetails'in kısaltılmış hali:
  // Sadece başlangıç + bitiş tarihi ve status
  const getRentalDetails = (crane) => {
    const activeRental = rentals.find(
      (r) => r.towerCraneId === crane.id && r.isActive
    );

    if (activeRental) {
      return {
        startDate: formatTrDate(activeRental.rentalStartDate),
        endDate: formatTrDate(activeRental.rentalEndDate),
        status: 'Inused', // kirada
        siteOrDepot: activeRental.workSite || '-',
      };
    } else {
      // aktif kiralama yok => boşta
      return {
        startDate: '-',
        endDate: '-',
        status: 'Active',
        siteOrDepot: crane.warehouse || '-',
      };
    }
  };

  // 1) Filtreleme mantığı: önce user input'a göre filtrele
  const filteredCranesByInput = towerCranes.filter((crane) => {
    const rental = getRentalDetails(crane);
    const brandName = getBrandName(crane.brandId);

    // Filtre alanları

    // 2) Marka
    if (filters.brand && brandName !== filters.brand) {
      return false;
    }
    // 3) Model
    if (filters.model && crane.model !== filters.model) {
      return false;
    }
    // 4) Yükseklik
    if (filters.height && crane.height.toString() !== filters.height) {
      return false;
    }
    // 5) Tonaj
    if (filters.tonnage && crane.maxTonnage.toString() !== filters.tonnage) {
      return false;
    }
    // 6) Status
    // Partner sayfasında da "all", "Active", "Inused" şeklinde filtre yapabiliyoruz
    if (filters.status !== 'all' && rental.status !== filters.status) {
      return false;
    }

    return true;
  });

  // 2) Partner kısıtlaması: sadece "Boşta (Active)" veya "1 ay içinde boşa düşecek" (Inused & nearEndDate)
  const partnerFilteredCranes = filteredCranesByInput.filter((crane) => {
    const rental = getRentalDetails(crane);
    const isActive = rental.status === 'Active'; // Boşta
    const nearEnd = isNearEndDate(rental.endDate); // 1 ay içinde bitecek
    return isActive || nearEnd;
  });

  // 3) Sıralama: Boşta > Kirada(1 aya yakın) > Kirada(uzak) (gerçi “uzak” zaten göstermiyoruz)
  // Ama TrackingPage'e benzer şekilde bir sıralama yapalım.
  const sortedCranes = [...partnerFilteredCranes].sort((a, b) => {
    const rentalA = getRentalDetails(a);
    const rentalB = getRentalDetails(b);

    // Boşta üstte
    if (rentalA.status === 'Active' && rentalB.status === 'Inused') return -1;
    if (rentalA.status === 'Inused' && rentalB.status === 'Active') return 1;

    // İkisi de Inused ise bitiş tarihi yakın olan üstte
    if (rentalA.status === 'Inused' && rentalB.status === 'Inused') {
      const endA = parseTrDateString(rentalA.endDate);
      const endB = parseTrDateString(rentalB.endDate);
      if (!endA || !endB) return 0;
      return endA - endB;
    }

    return 0;
  });

  // Filtre dropdown'lar için "unique" değerler:
  // (tracking page'de genelde crane.brandId'den brandName buluyorduk;
  //  bu nedenle unique brand'ları brandName olarak tutuyoruz)
  const allBrandNames = towerCranes.map((c) => getBrandName(c.brandId));
  const uniqueBrands = Array.from(new Set(allBrandNames)).filter((x) => x && x !== '-');

  const uniqueModels = Array.from(new Set(towerCranes.map((c) => c.model))).filter(Boolean);
  const uniqueHeights = Array.from(new Set(towerCranes.map((c) => c.height))).filter(Boolean);
  const uniqueTonnages = Array.from(new Set(towerCranes.map((c) => c.maxTonnage))).filter(Boolean);

  if (loading) {
    return (
      <div className="tracking-page">
        <Navbar />
        <div className="table-container">
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tracking-page">
      <Navbar />
      <div className="table-container">
        <h2 style={{ textAlign: 'center', marginBottom: '1rem' }}>
          Boştaki veya 1 Ay İçinde Boşa Düşecek Vinçler
        </h2>
        <table>
          <thead>
            <tr>
              <th>No</th>
              <th>Marka</th>
              <th>Model</th>
              <th>Yükseklik</th>
              <th>Tonaj</th>
              <th>Durum</th>
              <th>Kira Başlangıç</th>
              <th>Kira Bitiş</th>
              <th>Şantiye/Depo</th>
            </tr>
            {/* Filtre satırı */}
            <tr>
              <th />

              {/* Marka */}
              <th>
                <div className="select-container">
                  <select
                    value={filters.brand}
                    onChange={(e) => handleFilterChange(e, 'brand')}
                  >
                    <option value="">Tümü</option>
                    {uniqueBrands.map((brandName, i) => (
                      <option key={i} value={brandName}>
                        {brandName}
                      </option>
                    ))}
                  </select>
                </div>
              </th>
              {/* Model */}
              <th>
                <div className="select-container">
                  <select
                    value={filters.model}
                    onChange={(e) => handleFilterChange(e, 'model')}
                  >
                    <option value="">Tümü</option>
                    {uniqueModels.map((model, i) => (
                      <option key={i} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </div>
              </th>
              {/* Yükseklik */}
              <th>
                <div className="select-container">
                  <select
                    value={filters.height}
                    onChange={(e) => handleFilterChange(e, 'height')}
                  >
                    <option value="">Tümü</option>
                    {uniqueHeights.map((h, i) => (
                      <option key={i} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              </th>
              {/* Tonaj */}
              <th>
                <div className="select-container">
                  <select
                    value={filters.tonnage}
                    onChange={(e) => handleFilterChange(e, 'tonnage')}
                  >
                    <option value="">Tümü</option>
                    {uniqueTonnages.map((t, i) => (
                      <option key={i} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </th>
              {/* Durum */}
              <th>
                <div className="select-container">
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange(e, 'status')}
                  >
                    <option value="all">Tümü</option>
                    <option value="Active">Boşta</option>
                    <option value="Inused">Kirada</option>
                  </select>
                </div>
              </th>
              <th />
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {sortedCranes.map((crane, index) => {
              const rental = getRentalDetails(crane);
              const overdue = isOverdue(rental.endDate);
              const nearEnd = isNearEndDate(rental.endDate);

              // Satır renklendirme (TrackingPage’deki gibi)
              // row-active, row-used, row-warning
              let rowClass = '';
              if (rental.status === 'Active') {
                // Boşta
                rowClass = 'row-active';
              } else {
                // Kirada
                if (overdue) {
                  rowClass = 'row-used';
                } else if (nearEnd) {
                  rowClass = 'row-warning';
                } else {
                  rowClass = 'row-used';
                }
              }

              return (
                <tr key={crane.id} className={rowClass}>
                  <td>{index + 1}</td>
                  <td>{getBrandName(crane.brandId)}</td>
                  <td>{crane.model || '-'}</td>
                  <td>
                    {crane.height ? `${crane.height}m` : '-'}
                  </td>
                  <td>
                    {crane.maxTonnage ? `${crane.maxTonnage}T` : '-'}
                  </td>
                  <td>
                    {rental.status === 'Active' ? 'Boşta' : 'Kirada'}
                  </td>
                  <td>{rental.startDate}</td>
                  <td>{rental.endDate}</td>
                  <td>{rental.siteOrDepot}</td>
                </tr>
              );
            })}

            {sortedCranes.length === 0 && (
              <tr>
                <td colSpan={10}>Gösterilecek vinç bulunamadı.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Partner;
